import Jaws from '@modules/three/common/Jaws.ts';
import JawSegmentation from '@segmentation/three/JawSegmentation.ts';
import { JawSegmentationCreate } from '@/schema/jawSegmentation';
import { segmentationStore } from '@segmentation/store/segmentationStore';
import { geometryToPly } from '@modules/three/utils/meshExporter';

export default class JawsSegmentation extends Jaws {
  public lowerJaw: JawSegmentation;
  public upperJaw: JawSegmentation;
  private lowerJawData: { data: JawSegmentationCreate; teethModels: Blob[]; gumModel: Blob } = null!;
  private upperJawData: { data: JawSegmentationCreate; teethModels: Blob[]; gumModel: Blob } = null!;

  constructor(lowerJaw: JawSegmentation, upperJaw: JawSegmentation) {
    super(lowerJaw, upperJaw);
    this.lowerJaw = lowerJaw;
    this.upperJaw = upperJaw;
  }

  public async cutTeeth(jaw: 'upper' | 'lower') {
    segmentationStore.getState().startProcessing(jaw);
    try {
      if (jaw === 'upper') {
        this.upperJawData = await this.upperJaw.segmentJaw();
      } else {
        this.lowerJawData = await this.lowerJaw.segmentJaw();
      }
    } finally {
      segmentationStore.getState().stopProcessing();
    }
  }

  public getJawData() {
    return {
      lowerJaw: this.lowerJawData,
      upperJaw: this.upperJawData,
    };
  }

  /**
   * Re-exports the gum PLY blobs from the CURRENT jaw meshes. The blobs
   * cached by cutTeeth predate the review-step gum trim, so call this before
   * uploading — otherwise the untrimmed gum gets saved.
   *
   * Two invariants live here:
   * - the PLY comes from a fresh toBufferGeometry() build, never from
   *   jaw.geometry — computeBoundsTree reorders the live geometry's index in
   *   place, which would scatter the generated-face suffix through the file;
   * - generatedFacesFrom is recomputed alongside, because the trim may have
   *   removed scan faces and the cached pre-trim marker would then overhang
   *   into the generated block.
   */
  public async refreshGumModels() {
    if (this.lowerJawData) {
      this.lowerJawData.gumModel = await geometryToPly(this.lowerJaw.toBufferGeometry());
      const from = this.lowerJaw.getGeneratedFacesFrom();
      this.lowerJawData.data.generatedFacesFrom = from >= 0 ? from : null;
    }
    if (this.upperJawData) {
      this.upperJawData.gumModel = await geometryToPly(this.upperJaw.toBufferGeometry());
      const from = this.upperJaw.getGeneratedFacesFrom();
      this.upperJawData.data.generatedFacesFrom = from >= 0 ? from : null;
    }
  }

  public clearJawData(jaw: 'upper' | 'lower') {
    if (jaw === 'upper') {
      this.upperJawData = null!;
    } else {
      this.lowerJawData = null!;
    }
  }

  public revertJaw(jaw: 'upper' | 'lower', scanId: string | null) {
    const mesh = jaw === 'upper' ? this.upperJaw : this.lowerJaw;
    mesh.revertSegmentation(scanId);
    this.clearJawData(jaw);
  }

  public clearSegmentedTeethFromScene(jaw: 'upper' | 'lower') {
    const mesh = jaw === 'upper' ? this.upperJaw : this.lowerJaw;
    mesh.clearSegmentedTeethFromScene();
  }
}
